import { ContactConnectionInFormSchema } from "@/lib/data/schemas/contact/ContactSchema";
import { MatterInsuranceDataSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { MATTER_CONTACT_CONNECTION_DEFAULT_VALUES } from "@/lib/data/static/formStatics";
import { z } from "zod";

export const createInsuranceFormSchema = (
  enforceRequiredContact?: "Client" | "Defendant",
  clientContactId?: string,
  defendantContactIds?: string[],
  requiredPartyContactId?: string,
) =>
  MatterInsuranceDataSchema.omit({
    dateCreated: true,
    dateModified: true,
    matterId: true,
    id: true,
  })
    .extend({
      policyLimitCombined: z.string().optional(),
      umuimCombined: z.string().optional(),
      matterContactConnectionInsured: z.array(
        ContactConnectionInFormSchema.unwrap(),
      ),
      matterContactConnectionInsurer: ContactConnectionInFormSchema,
      matterContactConnectionPolicyHolder: ContactConnectionInFormSchema,
      matterContactConnectionBodilyInjuryAdjuster:
        ContactConnectionInFormSchema,
      matterContactConnectionPropertyDamageAdjuster:
        ContactConnectionInFormSchema,
      matterContactConnectionMedPayAdjuster: ContactConnectionInFormSchema,
      matterContactConnectionAdjusterSupervisor: ContactConnectionInFormSchema,
      matterContactConnectionSubrogationVendor: ContactConnectionInFormSchema,
      matterContactConnectionSubrogationRepresentative:
        ContactConnectionInFormSchema,
    })
    .superRefine((data, ctx) => {
      const coverageEnabled = data.coverageEnabled !== false;
      if (data.matterContactConnectionInsured.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: coverageEnabled
            ? "Insured is required"
            : "Uninsured is required",
          path: ["matterContactConnectionInsured"],
        });
      }
      if (coverageEnabled) {
        const trimmedRequiredPartyContactId = requiredPartyContactId?.trim();
        let requiredContactIds: string[] = trimmedRequiredPartyContactId
          ? [trimmedRequiredPartyContactId]
          : [];
        let requiredContactLabel = "Other party";

        if (enforceRequiredContact === "Client" && clientContactId) {
          requiredContactIds = [clientContactId];
          requiredContactLabel = "Client";
        } else if (
          enforceRequiredContact === "Defendant" &&
          defendantContactIds
        ) {
          requiredContactIds = defendantContactIds;
          requiredContactLabel = "Defendant";
        }

        if (requiredContactIds.length > 0) {
          const insuredContactIds = data.matterContactConnectionInsured.map(
            (contact) => contact.id,
          );
          const policyHolderContactId =
            data.matterContactConnectionPolicyHolder?.id;

          const hasClientAsInsured = requiredContactIds.some((contactId) =>
            insuredContactIds.includes(contactId),
          );
          const hasClientAsPolicyHolder = requiredContactIds.includes(
            policyHolderContactId || "",
          );

          if (!hasClientAsInsured && !hasClientAsPolicyHolder) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${requiredContactLabel} must be selected as either Insured or Policy Holder`,
              path: ["matterContactConnectionInsured"],
            });
          }
        }
      }
    });

export const insuranceDefaultFormValues = {
  type: "Auto",
  coverageEnabled: true,
  policyNumber: "",
  claimNumber: "",
  liability: "",
  liabilityPercentage: 100,
  medPay: false,
  medPayLimit: null,
  policyLimitConfirmation: false,
  policyLimitPerPerson: null,
  policyLimitPerIncident: null,
  umExists: false,
  umPerPerson: null,
  umPerIncident: null,
  uimExists: false,
  uimPerPerson: null,
  uimPerIncident: null,
  policyLimitCombined: "",
  category: "",
  matterContactConnectionInsured: [],
  matterContactConnectionInsurer: MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionPolicyHolder: MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionBodilyInjuryAdjuster:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionPropertyDamageAdjuster:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionMedPayAdjuster:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionAdjusterSupervisor:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionSubrogationVendor:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  matterContactConnectionSubrogationRepresentative:
    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
  groupNumber: "",
  memberNumber: "",
  subrogation: false,
};

export const contactIds = (...ids: (string | undefined)[]): string[] =>
  ids.filter((id): id is string => Boolean(id));

export const policyLimitMappings = {
  ["$50,000 / $100,000"]: {
    person: 50000,
    incident: 100000,
  },
  ["$100,000 / $200,000"]: {
    person: 100000,
    incident: 200000,
  },
  Other: {
    person: null,
    incident: null,
  },
};
